import { useEffect, useState } from "react";
import { Sparkles, Trash2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function LaunchDataReset({ onDone }) {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sampleOpen, setSampleOpen] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const load = () => api.get("/admin/launch-data-summary").then((r) => setSummary(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const run = async (mode) => {
    setBusy(true);
    try {
      const body = mode === "all" ? { mode, confirm: confirmText } : { mode };
      const r = await api.post("/admin/reset-launch-data", body);
      toast.success(`Removed ${r.data.waitlist_deleted} signups and ${r.data.providers_deleted} providers`);
      setSampleOpen(false); setFullOpen(false); setConfirmText("");
      await load();
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const sampleCount = (summary?.waitlist_sample ?? 0) + (summary?.providers_sample ?? 0);

  return (
    <div className="mt-6 rounded-2xl border border-amber-500/20 bg-amber-50/40 p-6" data-testid="launch-data-reset">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <p className="text-sm font-semibold text-neutral-800">Launch data</p>
      </div>
      <p className="mt-1 text-sm text-neutral-500">
        {summary
          ? `${summary.waitlist_sample} sample signups and ${summary.providers_sample} sample providers detected · ${summary.waitlist_real} real signups, ${summary.providers_real} real providers.`
          : "Checking data…"}
      </p>

      <div className="mt-4 flex flex-wrap gap-3">
        <Button
          onClick={() => setSampleOpen(true)}
          disabled={sampleCount === 0}
          className="gap-2 rounded-xl bg-brand hover:bg-brand-dark"
          data-testid="clear-sample-btn">
          <Sparkles className="h-4 w-4" /> Clear sample data
        </Button>
        <Button
          onClick={() => setFullOpen(true)}
          variant="outline"
          className="gap-2 rounded-xl border-sos/30 text-sos hover:bg-sos/5"
          data-testid="full-reset-btn">
          <Trash2 className="h-4 w-4" /> Full reset to zero
        </Button>
      </div>

      {/* Clear sample confirm */}
      <Dialog open={sampleOpen} onOpenChange={setSampleOpen}>
        <DialogContent data-testid="clear-sample-dialog">
          <DialogHeader>
            <DialogTitle>Clear sample data?</DialogTitle>
            <DialogDescription>
              This removes only the seeded demo entries (emails ending in @demo.sos). Any real signups and providers are kept. This can’t be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSampleOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={() => run("sample")} disabled={busy} className="gap-2 bg-brand hover:bg-brand-dark" data-testid="clear-sample-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Clear sample data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full reset confirm */}
      <Dialog open={fullOpen} onOpenChange={(o) => { setFullOpen(o); if (!o) setConfirmText(""); }}>
        <DialogContent data-testid="full-reset-dialog">
          <DialogHeader>
            <DialogTitle>Full reset to zero?</DialogTitle>
            <DialogDescription>
              This permanently deletes ALL waitlist signups and provider applications — real and sample. Your stats will start from zero. Type <strong>RESET</strong> to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Type RESET"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            data-testid="full-reset-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setFullOpen(false); setConfirmText(""); }} disabled={busy}>Cancel</Button>
            <Button onClick={() => run("all")} disabled={busy || confirmText !== "RESET"} className="gap-2 bg-sos hover:bg-sos/90" data-testid="full-reset-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
